import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  IconButton,
  LinearProgress,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import {
  CloseRounded,
  LockRounded,
  AutoAwesomeRounded,
  DownloadRounded,
} from '@mui/icons-material';
import { useTenantStore } from '../../../stores/tenant.store';
import {
  surveyApi,
  type QuestionAnalyticsDto,
  type QuestionHealth,
  type SurveyAnalyticsDto,
  type SurveyBenchmarkDto,
} from '../../../lib/api/survey.api';
import { downloadCsv } from '../util/csv';

interface Props {
  surveyId: string;
  title: string;
  onClose: () => void;
}

const HEALTH_COLOR: Record<QuestionHealth, 'success' | 'warning' | 'error' | 'default'> = {
  good: 'success',
  warn: 'warning',
  bad: 'error',
  na: 'default',
};

export function SurveyAnalyticsDialog({ surveyId, title, onClose }: Props) {
  const { t } = useTranslation('sis');
  const { currentTenantId } = useTenantStore();

  const { data, isLoading } = useQuery({
    queryKey: ['survey-analytics', currentTenantId, surveyId],
    queryFn: () => surveyApi.analytics({ tenantId: currentTenantId!, surveyId }),
    enabled: !!currentTenantId,
  });

  const queryClient = useQueryClient();
  const summarize = useMutation({
    mutationFn: () => surveyApi.summarizeComments({ tenantId: currentTenantId!, surveyId }),
  });

  const benchmarkQ = useQuery({
    queryKey: ['survey-benchmark', currentTenantId, surveyId],
    queryFn: () => surveyApi.benchmark({ tenantId: currentTenantId!, surveyId }),
    enabled: !!currentTenantId && !!data && !data.belowPrivacyFloor,
  });

  const [actionNote, setActionNote] = useState('');
  const [notify, setNotify] = useState(false);
  const recordAction = useMutation({
    mutationFn: () =>
      surveyApi.recordAction({
        tenantId: currentTenantId!,
        surveyId,
        note: actionNote,
        notifyStudents: notify,
      }),
    onSuccess: () => {
      setActionNote('');
      queryClient.invalidateQueries({ queryKey: ['survey-analytics', currentTenantId, surveyId] });
    },
  });

  const ratePct = data ? Math.round(data.responseRate * 100) : 0;
  const hasTextQuestions = (data?.survey.questions ?? []).some((q) => q.type === 'text');

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ pr: 6 }}>
        {title}
        {data && !data.belowPrivacyFloor && data.responseCount > 0 && (
          <IconButton
            size="small"
            title={t('surveys.exportCsv')}
            onClick={() => exportAnalyticsCsv(title, data)}
            sx={{ ml: 1 }}
          >
            <DownloadRounded fontSize="small" />
          </IconButton>
        )}
        <IconButton onClick={onClose} sx={{ position: 'absolute', right: 8, top: 8 }}>
          <CloseRounded />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {isLoading || !data ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Stack spacing={3}>
            <Stack direction="row" spacing={3} flexWrap="wrap">
              <StatChip
                label={t('surveys.analytics.responses')}
                value={String(data.responseCount)}
              />
              <StatChip
                label={t('surveys.analytics.eligible')}
                value={String(data.eligibleCount)}
              />
              <StatChip label={t('surveys.analytics.rate')} value={`${ratePct}%`} />
            </Stack>
            <Chip
              size="small"
              icon={<LockRounded />}
              color="success"
              variant="outlined"
              label={t('surveys.analytics.anonymousNote')}
              sx={{ alignSelf: 'flex-start' }}
            />

            {data.responseCount === 0 ? (
              <Typography color="text.secondary">{t('surveys.analytics.noData')}</Typography>
            ) : data.belowPrivacyFloor ? (
              <Alert severity="info" icon={<LockRounded />}>
                {t('surveys.analytics.privacyFloor', {
                  floor: data.privacyFloor,
                  count: data.responseCount,
                })}
              </Alert>
            ) : (
              <>
                {benchmarkQ.data && <BenchmarkBlock data={benchmarkQ.data} />}

                <Stack spacing={3} divider={<Divider flexItem />}>
                  {data.questions.map((q) => (
                    <QuestionResult key={q.questionId} q={q} total={data.responseCount} />
                  ))}
                </Stack>

                {hasTextQuestions && (
                  <Box>
                    <Divider sx={{ mb: 2 }} />
                    <Button
                      variant="outlined"
                      startIcon={<AutoAwesomeRounded />}
                      onClick={() => summarize.mutate()}
                      disabled={summarize.isPending}
                    >
                      {summarize.isPending
                        ? t('surveys.analytics.summarizing')
                        : t('surveys.analytics.summarize')}
                    </Button>
                    {summarize.isError && (
                      <Alert severity="error" sx={{ mt: 2 }}>
                        {t('surveys.analytics.summaryError')}
                      </Alert>
                    )}
                    {summarize.data && <CommentSummary data={summarize.data} />}
                  </Box>
                )}

                {/* Close the loop */}
                <Box>
                  <Divider sx={{ mb: 2 }} />
                  <Typography variant="subtitle2" fontWeight={700} gutterBottom>
                    {t('surveys.action.title')}
                  </Typography>
                  {data.survey.actionNote ? (
                    <Alert severity="success" sx={{ mt: 1 }}>
                      {data.survey.actionNote}
                    </Alert>
                  ) : (
                    <Stack spacing={1.5} sx={{ mt: 1 }}>
                      <Typography variant="body2" color="text.secondary">
                        {t('surveys.action.hint')}
                      </Typography>
                      <TextField
                        multiline
                        minRows={2}
                        placeholder={t('surveys.action.placeholder')}
                        value={actionNote}
                        onChange={(e) => setActionNote(e.target.value)}
                        fullWidth
                      />
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={notify}
                            onChange={(e) => setNotify(e.target.checked)}
                          />
                        }
                        label={t('surveys.action.notify')}
                      />
                      <Box>
                        <Button
                          variant="contained"
                          disabled={!actionNote.trim() || recordAction.isPending}
                          onClick={() => recordAction.mutate()}
                        >
                          {recordAction.isPending
                            ? t('surveys.action.saving')
                            : t('surveys.action.save')}
                        </Button>
                      </Box>
                    </Stack>
                  )}
                </Box>
              </>
            )}
          </Stack>
        )}
      </DialogContent>
    </Dialog>
  );
}

function BenchmarkBlock({ data }: { data: SurveyBenchmarkDto }) {
  const { t } = useTranslation('sis');
  const pct = (v?: number) => (v !== undefined ? `${Math.round(v * 100)}%` : '—');
  const delta =
    data.selfShare !== undefined && data.institutionAvg !== undefined
      ? Math.round((data.selfShare - data.institutionAvg) * 100)
      : undefined;
  return (
    <Box sx={{ p: 2, bgcolor: 'action.hover', borderRadius: 2 }}>
      <Typography variant="subtitle2" fontWeight={700} gutterBottom>
        {t('surveys.benchmark.title')}
      </Typography>
      <Stack direction="row" spacing={3} flexWrap="wrap">
        <BenchStat label={t('surveys.benchmark.self')} value={pct(data.selfShare)} strong />
        <BenchStat label={t('surveys.benchmark.course')} value={pct(data.courseAvg)} />
        <BenchStat label={t('surveys.benchmark.faculty')} value={pct(data.facultyAvg)} />
        <BenchStat label={t('surveys.benchmark.institution')} value={pct(data.institutionAvg)} />
      </Stack>
      {delta !== undefined && delta !== 0 && (
        <Typography
          variant="caption"
          color={delta > 0 ? 'success.main' : 'error.main'}
          sx={{ mt: 1, display: 'block' }}
        >
          {t(delta > 0 ? 'surveys.benchmark.above' : 'surveys.benchmark.below', {
            n: Math.abs(delta),
          })}
        </Typography>
      )}
    </Box>
  );
}

function BenchStat({ label, value, strong }: { label: string; value: string; strong?: boolean }) {
  return (
    <Box>
      <Typography variant="h6" fontWeight={strong ? 800 : 600}>
        {value}
      </Typography>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
    </Box>
  );
}

function StatChip({ label, value }: { label: string; value: string }) {
  return (
    <Box>
      <Typography variant="h5" fontWeight={700}>
        {value}
      </Typography>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
    </Box>
  );
}

function QuestionResult({ q, total }: { q: QuestionAnalyticsDto; total: number }) {
  const { t } = useTranslation('sis');
  const isScale = q.type === 'likert' || q.type === 'nps' || q.type === 'stars';
  const favPct =
    q.favorable !== undefined && q.count > 0
      ? Math.round((q.favorable / q.count) * 100)
      : undefined;

  return (
    <Box>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
        <Typography variant="subtitle2" fontWeight={700} sx={{ flex: 1 }}>
          {q.label}
        </Typography>
        {q.health && q.health !== 'na' && (
          <Chip
            size="small"
            color={HEALTH_COLOR[q.health]}
            label={t(`surveys.analytics.health.${q.health}`)}
          />
        )}
      </Stack>

      {isScale && (
        <Stack direction="row" spacing={2} sx={{ mb: 1 }} flexWrap="wrap">
          {q.average !== undefined && (
            <Typography variant="body2">
              {t('surveys.analytics.average')}: <strong>{q.average.toFixed(1)}</strong>
            </Typography>
          )}
          {favPct !== undefined && (
            <Typography variant="body2">
              {t('surveys.analytics.favorable')}: <strong>{favPct}%</strong>
            </Typography>
          )}
          {q.npsScore !== undefined && (
            <Typography variant="body2">
              NPS: <strong>{q.npsScore}</strong>
            </Typography>
          )}
        </Stack>
      )}

      {q.distribution && Object.keys(q.distribution).length > 0 && (
        <Stack spacing={0.75}>
          {Object.entries(q.distribution)
            .sort((a, b) => sortKey(a[0]) - sortKey(b[0]))
            .map(([key, count]) => {
              const pct = total > 0 ? Math.round((count / total) * 100) : 0;
              return (
                <Box key={key}>
                  <Stack direction="row" justifyContent="space-between">
                    <Typography variant="caption">{key}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {count} · {pct}%
                    </Typography>
                  </Stack>
                  <LinearProgress
                    variant="determinate"
                    value={pct}
                    sx={{ height: 8, borderRadius: 1 }}
                  />
                </Box>
              );
            })}
        </Stack>
      )}

      {q.type === 'text' && (
        <Stack spacing={1} sx={{ mt: 1 }}>
          {(q.comments ?? []).length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              {t('surveys.analytics.noComments')}
            </Typography>
          ) : (
            (q.comments ?? []).map((c, i) => (
              <Box
                key={i}
                sx={{ p: 1.5, bgcolor: 'action.hover', borderRadius: 2, fontStyle: 'italic' }}
              >
                <Typography variant="body2">“{c}”</Typography>
              </Box>
            ))
          )}
        </Stack>
      )}
    </Box>
  );
}

function CommentSummary({
  data,
}: {
  data: import('../../../lib/api/survey.api').CommentSummaryDto;
}) {
  const { t } = useTranslation('sis');
  const sentimentColor = { positive: 'success', neutral: 'default', negative: 'error' } as const;

  if (data.status === 'insufficient') {
    return (
      <Alert severity="info" sx={{ mt: 2 }}>
        {t('surveys.analytics.summaryInsufficient')}
      </Alert>
    );
  }
  return (
    <Box sx={{ mt: 2, p: 2, bgcolor: 'action.hover', borderRadius: 2 }}>
      {data.overallSentiment && (
        <Chip
          size="small"
          sx={{ mb: 1.5 }}
          color={
            sentimentColor[data.overallSentiment === 'mixed' ? 'neutral' : data.overallSentiment]
          }
          label={t(`surveys.analytics.sentiment.${data.overallSentiment}`)}
        />
      )}
      {data.summary && (
        <Typography variant="body2" sx={{ mb: 2 }}>
          {data.summary}
        </Typography>
      )}
      {data.costEstimateUsd !== undefined && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
          {t('surveys.analytics.aiCost', { cost: data.costEstimateUsd.toFixed(4) })}
        </Typography>
      )}
      <Stack spacing={1}>
        {data.themes.map((th, i) => (
          <Stack key={i} direction="row" spacing={1} alignItems="center">
            <Chip
              size="small"
              variant="outlined"
              color={sentimentColor[th.sentiment]}
              label={t(`surveys.analytics.sentiment.${th.sentiment}`)}
            />
            <Typography variant="body2" sx={{ flex: 1 }}>
              {th.title}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {t('surveys.analytics.mentions', { count: th.mentions })}
            </Typography>
          </Stack>
        ))}
      </Stack>
    </Box>
  );
}

// Numeric scale keys sort numerically; text choices keep insertion order.
function sortKey(k: string): number {
  const n = Number(k);
  return Number.isNaN(n) ? 0 : n;
}

/** Flatten the analytics into a CSV (one row per question metric + a header). */
function exportAnalyticsCsv(title: string, data: SurveyAnalyticsDto): void {
  const rows: (string | number)[][] = [
    ['Encuesta', title],
    ['Respuestas', data.responseCount],
    ['Alumnos', data.eligibleCount],
    ['Tasa', `${Math.round(data.responseRate * 100)}%`],
    [],
    ['Pregunta', 'Tipo', 'Respuestas', 'Promedio', 'Favorable', 'NPS', 'Valor', 'Conteo'],
  ];
  for (const q of data.questions) {
    const baseline = [
      q.label,
      q.type,
      q.count,
      q.average !== undefined ? q.average.toFixed(2) : '',
      q.favorable ?? '',
      q.npsScore ?? '',
    ];
    if (q.distribution && Object.keys(q.distribution).length > 0) {
      for (const [value, count] of Object.entries(q.distribution)) {
        rows.push([...baseline, value, count]);
      }
    } else if (q.comments?.length) {
      for (const c of q.comments) rows.push([...baseline, 'comentario', c]);
    } else {
      rows.push([...baseline, '', '']);
    }
  }
  downloadCsv(`encuesta-${title}`, rows);
}
